import { useState, useMemo } from 'react';
import { useGetPrograms, type Program } from '../api/useGetPrograms';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { programStatus } from '../lib/programStatus';
import { useFittedPageSize } from '../hooks/useFittedPageSize';

const ALL_LIMIT = 200;

interface OrganizerRow {
  name: string;
  programs: number;
  live: number;
}

const columns: Column<OrganizerRow>[] = [
  { key: 'name', header: 'Organizer', className: 'text-gray-900', cell: (o) => o.name, copyValue: (o) => o.name },
  { key: 'programs', header: 'Programs', cell: (o) => o.programs.toLocaleString() },
  { key: 'live', header: 'Live now', cell: (o) => o.live.toLocaleString() },
];

function aggregateOrganizers(programs: Program[]): OrganizerRow[] {
  const byName = new Map<string, OrganizerRow>();
  for (const program of programs) {
    const name = program.merchant_name || '(unknown)';
    const row = byName.get(name) ?? { name, programs: 0, live: 0 };
    row.programs += 1;
    if (programStatus(program) === 'Live') row.live += 1;
    byName.set(name, row);
  }
  return Array.from(byName.values()).sort((a, b) => b.programs - a.programs);
}

export const OrganizersTable = () => {
  const [page, setPage] = useState(0);
  const { tableRef, pageSize } = useFittedPageSize();
  const { data, isLoading, isError } = useGetPrograms({ page: 0, limit: ALL_LIMIT });

  const organizers = useMemo(
    () => aggregateOrganizers(data?.payload?.programs ?? []),
    [data]
  );
  const ready = !isLoading && !isError;
  const start = page * pageSize;
  const pageRows = organizers.slice(start, start + pageSize);
  const showPagination = ready && organizers.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {ready && (
        <span className="text-sm text-gray-500">{organizers.length} organizers</span>
      )}
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={pageRows}
          rowKey={(o) => o.name}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load organizers."
          emptyLabel="No organizers found."
        />
      </div>
      {showPagination && (
        <Pagination page={page} pageSize={pageSize} total={organizers.length} onPageChange={setPage} />
      )}
    </div>
  );
};
